import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, PencilSimple, Trash, UploadSimple, DownloadSimple, MagnifyingGlass, Globe, WarningCircle, CheckCircle, Eye, EyeSlash, Buildings, Broom, Prohibit, ArrowCounterClockwise, Sparkle, CaretUp, CaretDown, CaretUpDown, CaretLeft, CaretRight } from "@phosphor-icons/react";
import SignalImportWizard from "@/components/admin/SignalImportWizard";
import ContactImportWizard from "@/components/admin/ContactImportWizard";
import CompanyImportWizard from "@/components/admin/CompanyImportWizard";
import OfficesDialog from "@/components/admin/OfficesDialog";
import { MapPin } from "@phosphor-icons/react";

const SENIORITY = ["Débutant", "Junior", "Confirmé", "Senior", "Expert"];
const CONTRACT = ["Régie", "Forfait"];
const CONTACT_SENIORITY = ["Décideur", "Opérationnel"];
const MISSION_TYPES = ["Client final", "Intermédiaire", "CDI", "CDD", "Freelance", "Régie", "Contrat", "Stage", "Alternance", "Intérim"];
const MISSION_TYPE_OPTIONS = ["Régie", "CDI", "CDD", "Freelance", "Contrat", "Stage", "Alternance", "Intérim"];
const EXCLUSION_REASONS = ["Agrégateur bruyant", "Hors cible", "Doublon / entité fusionnée", "Concurrent", "Autre"];

// Provenance : libellés des fournisseurs d'ingestion (canaux de collecte interKO).
const PROVIDER_LABELS = {
  JSEARCH: "JSearch", THEIRSTACK: "TheirStack", FANTASTIC_JOBS: "Fantastic.jobs",
  FRANCE_TRAVAIL: "France Travail", HASDATA: "Indeed (HasData)", SERPAPI: "Google Jobs (SerpApi)",
  ADZUNA: "Adzuna", BRIGHTDATA: "Bright Data", BRIGHT_DATA: "Bright Data", LEGACY: "Import",
};
function providerLabel(code) {
  return PROVIDER_LABELS[code] || (code || "").replace(/_/g, " ");
}

const inputCls = "bg-surface-2 border-border text-fg";

// Format court de la date de publication d'un signal (JJ/MM/AAAA).
function fmtSignalDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function Field({ label, children }) {
  return (
    <div>
      <label className="overline block mb-1.5">{label}</label>
      {children}
    </div>
  );
}

// Bandeau global "Patterns email" : stats + backfill (Recalculer depuis tout l'historique de contacts).
function EmailPatternStats() {
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.get("/admin/email-patterns/stats").then(({ data }) => setStats(data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const recalc = async () => {
    if (!window.confirm("Recalculer les patterns email ET déduire les emails manquants depuis tout l'historique de contacts ? (aucun crédit provider)")) return;
    setBusy(true);
    try {
      await api.post("/admin/email-patterns/recalc");
      toast.success("Recalcul + déduction lancés en arrière-plan — actualisez dans quelques secondes");
      setTimeout(load, 6000);
    } catch { toast.error("Erreur"); } finally { setBusy(false); }
  };
  if (!stats) return null;
  const Stat = ({ label, value, tid }) => (
    <div className="text-center"><div className="font-display text-xl font-bold text-accent" data-testid={tid}>{value}</div><div className="text-[10px] text-muted overline">{label}</div></div>
  );
  return (
    <div className="surface rounded-lg p-4 mb-4 flex items-center justify-between gap-4 flex-wrap" data-testid="email-pattern-stats">
      <div className="flex items-center gap-6 flex-wrap">
        <div className="overline flex items-center gap-2"><Sparkle size={14} weight="duotone" className="text-accent" /> Patterns email</div>
        <Stat label="Entreprises avec pattern" value={stats.companies_with_pattern} tid="eps-companies" />
        <Stat label="Utilisables" value={stats.buildable} tid="eps-buildable" />
        <Stat label="Confirmés admin" value={stats.verified} tid="eps-verified" />
        <Stat label="Emails générés" value={stats.emails_generated} tid="eps-generated" />
        <Stat label="Appels Prospeo évités" value={stats.external_lookups_avoided} tid="eps-avoided" />
      </div>
      <button onClick={recalc} disabled={busy} data-testid="email-pattern-recalc-btn"
        className="flex items-center gap-1.5 text-xs font-semibold border border-accent/40 text-accent px-3 py-1.5 rounded-md hover-lift disabled:opacity-60">
        <ArrowCounterClockwise size={13} weight="bold" /> {busy ? "Lancement…" : "Recalculer + déduire les emails"}
      </button>
    </div>
  );
}

// Recherche + sélection d'entreprise (taper les premières lettres). Option "créer" ad-hoc.
function CompanyCombobox({ value, displayName, onSelect, allowCreate = true, placeholder }) {
  const [q, setQ] = useState(displayName || "");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [creating, setCreating] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const h = setTimeout(async () => {
      const term = q.trim();
      setSearching(true);
      try {
        const { data } = await api.get("/admin/companies", { params: { limit: 8, ...(term ? { q: term } : {}) } });
        setResults(data.items || data || []);
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 220);
    return () => clearTimeout(h);
  }, [q, open]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (c) => { onSelect(c.id, c.name, c); setQ(c.name); setOpen(false); };
  const exact = results.some((r) => (r.name || "").trim().toLowerCase() === q.trim().toLowerCase());
  const canCreate = allowCreate && q.trim().length >= 2 && !exact;

  const createAdhoc = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/admin/companies", { name: q.trim() });
      toast.success(`Entreprise « ${data.name} » créée`);
      onSelect(data.id, data.name, data);
      setQ(data.name);
      setOpen(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setCreating(false); }
  };

  return (
    <div className="relative" ref={boxRef} data-testid="company-combobox">
      <div className="relative">
        <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
        <Input className={inputCls + " pl-9 pr-8"} value={q} data-testid="company-combobox-input"
          onChange={(e) => { setQ(e.target.value); setOpen(true); if (value) onSelect("", ""); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder || "Tapez les premières lettres de l'entreprise…"} />
        {value ? <CheckCircle size={16} weight="fill" className="absolute right-2.5 top-1/2 -translate-y-1/2 text-accent" /> : null}
      </div>
      {open && (
        <div className="absolute z-[60] mt-1 w-full bg-surface border border-border rounded-md shadow-xl max-h-64 overflow-y-auto" data-testid="company-combobox-list">
          {searching && <div className="px-3 py-2 text-xs text-muted">Recherche…</div>}
          {!searching && results.map((c) => (
            <button type="button" key={c.id} onClick={() => pick(c)} data-testid={`company-option-${c.id}`}
              className="w-full text-left px-3 py-2 text-sm text-fg hover:bg-surface-2 flex items-center justify-between gap-2">
              <span className="truncate flex items-center gap-1.5"><Buildings size={13} className="text-muted shrink-0" />{c.name}</span>
              {c.city ? <span className="text-[10px] text-muted font-mono shrink-0">{c.city}</span> : null}
            </button>
          ))}
          {!searching && !results.length && !canCreate && (
            <div className="px-3 py-2 text-xs text-muted">{q.trim() ? "Aucune entreprise trouvée." : "Tapez pour rechercher…"}</div>
          )}
          {canCreate && (
            <button type="button" onClick={createAdhoc} disabled={creating} data-testid="company-combobox-create"
              className="w-full text-left px-3 py-2 text-sm border-t border-border text-accent hover:bg-accent/10 flex items-center gap-2 disabled:opacity-60">
              <Plus size={14} weight="bold" /> {creating ? "Création…" : `Créer l'entreprise « ${q.trim()} »`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminContent() {
  const [tab, setTab] = useState("companies");
  const [companies, setCompanies] = useState([]);
  const [signals, setSignals] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [importOpen, setImportOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [contactWizardOpen, setContactWizardOpen] = useState(false);
  const [companyWizardOpen, setCompanyWizardOpen] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [importing, setImporting] = useState(false);
  const [query, setQuery] = useState("");
  const [incomplete, setIncomplete] = useState([]);
  const [incompleteCount, setIncompleteCount] = useState(0);
  const [domainFailedCount, setDomainFailedCount] = useState(0);
  const [enriching, setEnriching] = useState(null);
  const [gate, setGate] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [missionFilter, setMissionFilter] = useState("all");
  const [providerFilter, setProviderFilter] = useState("all");
  // Pagination + tri + filtres par colonne (server-side)
  const SORT_DEFAULTS = { companies: { sort: "name", order: "asc" }, signals: { sort: "posted_at", order: "desc" }, contacts: { sort: "name", order: "asc" } };
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sort, setSort] = useState("name");
  const [order, setOrder] = useState("asc");
  const [sigCompany, setSigCompany] = useState("");
  const [sigCity, setSigCity] = useState("");
  const [sigClientType, setSigClientType] = useState("all");
  const [coCity, setCoCity] = useState("");
  const [coType, setCoType] = useState("all");
  const [coSector, setCoSector] = useState("");
  const [ctCompany, setCtCompany] = useState("");
  const [ctCity, setCtCity] = useState("");
  const [ctSeniority, setCtSeniority] = useState("all");
  const [ctHasEmail, setCtHasEmail] = useState("all");
  const [sigSel, setSigSel] = useState([]);
  const [ctSel, setCtSel] = useState([]);
  const [bulkSigType, setBulkSigType] = useState("");
  const [bulkCtType, setBulkCtType] = useState("");
  const [bulkRowBusy, setBulkRowBusy] = useState(false);
  const [esnVis, setEsnVis] = useState(null);
  const [counts, setCounts] = useState({ companies: 0, signals: 0, contacts: 0 });
  const [activeTotal, setActiveTotal] = useState(0);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [dl, setDl] = useState(false);
  const [dedupOpen, setDedupOpen] = useState(false);
  const [dedupPreview, setDedupPreview] = useState(null);
  const [dedupLoading, setDedupLoading] = useState(false);
  const [dedupJob, setDedupJob] = useState(null);
  const [officesFor, setOfficesFor] = useState(null);
  const [formOffices, setFormOffices] = useState([]);
  const [formOfficesLoading, setFormOfficesLoading] = useState(false);
  const [domainFailed, setDomainFailed] = useState([]);
  const [mergeSel, setMergeSel] = useState([]);
  const [mergeTarget, setMergeTarget] = useState("");
  const [merging, setMerging] = useState(false);
  const [excluded, setExcluded] = useState([]);
  const [excludedCount, setExcludedCount] = useState(0);
  const [excludeTarget, setExcludeTarget] = useState(null);
  const [excludeForm, setExcludeForm] = useState({ reason: "", note: "", cleanup: "keep" });
  const [excludeBusy, setExcludeBusy] = useState(false);
  const firstRender = useRef(true);

  const loadIncompleteCount = useCallback(async () => {
    try { const { data } = await api.get("/admin/signals/incomplete-count"); setIncompleteCount(data.signals || 0); } catch { /* noop */ }
  }, []);
  const loadDomainFailedCount = useCallback(async () => {
    try { const { data } = await api.get("/admin/companies/domain-failed"); setDomainFailedCount(data.total || 0); } catch { /* noop */ }
  }, []);
  const loadExcludedCount = useCallback(async () => {
    try { const { data } = await api.get("/admin/companies/excluded"); setExcludedCount(data.total || 0); } catch { /* noop */ }
  }, []);
  const loadGate = useCallback(async () => {
    try { const { data } = await api.get("/admin/domains/gate"); setGate(data); } catch { /* noop */ }
  }, []);
  const loadEsnVis = useCallback(async () => {
    try { const { data } = await api.get("/admin/esn-visibility/mission-types"); setEsnVis(data); } catch { /* noop */ }
  }, []);
  const loadCounts = useCallback(async () => {
    try { const { data } = await api.get("/admin/content-counts"); setCounts(data); } catch { /* noop */ }
  }, []);
  const loadOptions = useCallback(async () => {
    try { const { data } = await api.get("/admin/companies/options"); setCompanyOptions(data); } catch { /* noop */ }
  }, []);

  const openDedup = async () => {
    setDedupOpen(true); setDedupPreview(null); setDedupJob(null); setDedupLoading(true);
    try {
      const { data } = await api.get("/admin/dedup/preview");
      setDedupPreview(data);
    } catch (e) { toast.error(formatApiErrorDetail(e) || "Aperçu impossible"); }
    finally { setDedupLoading(false); }
  };

  const pollDedup = useCallback(async (jobId) => {
    try {
      const { data } = await api.get(`/admin/dedup/status/${jobId}`);
      setDedupJob(data);
      if (data.status === "running") { setTimeout(() => pollDedup(jobId), 1200); return; }
      if (data.status === "done") {
        toast.success(`Fusion terminée · ${data.merged_companies || 0} entreprise(s), ${data.merged_contacts || 0} contact(s) supprimé(s)`);
        loadCounts(); fetchTab(tab, query);
      } else if (data.status === "error") {
        toast.error(`Échec : ${data.message || "erreur"}`);
      }
    } catch { setTimeout(() => pollDedup(jobId), 2000); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, query]);

  const runDedup = async () => {
    try {
      const { data } = await api.post("/admin/dedup/run");
      setDedupJob({ status: "running", phase: "starting" });
      pollDedup(data.job_id);
    } catch (e) { toast.error(formatApiErrorDetail(e) || "Lancement impossible"); }
  };

  const setterFor = (t) => ({ companies: setCompanies, signals: setSignals, contacts: setContacts, incomplete: setIncomplete, "domain-failed": setDomainFailed, excluded: setExcluded }[t]);

  const fetchTab = useCallback(async (t, qv = "") => {
    if (t === "incomplete") {
      const { data } = await api.get("/admin/signals", { params: { incomplete: true, ...(qv ? { q: qv } : {}) } });
      setIncomplete(data.items || data);
      return;
    }
    if (t === "domain-failed") {
      const { data } = await api.get("/admin/companies/domain-failed", { params: { ...(qv ? { q: qv } : {}) } });
      setDomainFailed(data.items || data);
      return;
    }
    if (t === "excluded") {
      const { data } = await api.get("/admin/companies/excluded", { params: { ...(qv ? { q: qv } : {}) } });
      setExcluded(data.items || data);
      return;
    }
    const ep = t === "companies" ? "/admin/companies" : t === "contacts" ? "/admin/contacts" : "/admin/signals";
    const params = { limit: pageSize, offset: (page - 1) * pageSize, sort, order };
    if (qv) params.q = qv;
    if (t === "signals") {
      if (missionFilter && missionFilter !== "all") params.mission_type = missionFilter;
      if (providerFilter && providerFilter !== "all") params.provider = providerFilter;
      if (sigCompany.trim()) params.company = sigCompany.trim();
      if (sigCity.trim()) params.city = sigCity.trim();
      if (sigClientType !== "all") params.client_type = sigClientType;
    }
    if (t === "companies") {
      if (coCity.trim()) params.city = coCity.trim();
      if (coType !== "all") params.company_class = coType;
      if (coSector.trim()) params.sector = coSector.trim();
    }
    if (t === "contacts") {
      if (ctCompany.trim()) params.company = ctCompany.trim();
      if (ctCity.trim()) params.city = ctCity.trim();
      if (ctSeniority !== "all") params.seniority = ctSeniority;
      if (ctHasEmail !== "all") params.has_email = ctHasEmail;
    }
    const { data } = await api.get(ep, { params });
    const items = data.items || data;
    setActiveTotal(data.total ?? items.length);
    setterFor(t)(items);
  }, [page, pageSize, sort, order, missionFilter, providerFilter, sigCompany, sigCity, sigClientType, coCity, coType, coSector, ctCompany, ctCity, ctSeniority, ctHasEmail]);

  // Tri par colonne
  const toggleSort = (field) => {
    if (sort === field) setOrder((o) => (o === "asc" ? "desc" : "asc"));
    else { setSort(field); setOrder(field === "posted_at" ? "desc" : "asc"); }
  };

  const refresh = useCallback(() => {
    fetchTab(tab, query.trim()).catch(() => {});
    loadCounts(); loadOptions(); loadIncompleteCount(); loadDomainFailedCount(); loadExcludedCount();
  }, [tab, query, fetchTab, loadCounts, loadOptions, loadIncompleteCount, loadDomainFailedCount, loadExcludedCount]);

  const changeTab = (v) => {
    setQuery(""); setSigSel([]); setCtSel([]);
    setSigCompany(""); setSigCity(""); setSigClientType("all"); setMissionFilter("all"); setProviderFilter("all");
    setCoCity(""); setCoType("all"); setCoSector("");
    setCtCompany(""); setCtCity(""); setCtSeniority("all"); setCtHasEmail("all");
    const d = SORT_DEFAULTS[v] || { sort: "name", order: "asc" };
    setSort(d.sort); setOrder(d.order); setPage(1);
    setTab(v);
  };

  // Sélection en masse — Signaux
  const toggleSig = (id) => setSigSel((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const toggleAllSig = () => setSigSel((p) => (p.length === signals.length ? [] : signals.map((s) => s.id)));
  const runSigBulk = async (action, value) => {
    if (!sigSel.length) return;
    if (action === "delete" && !window.confirm(`Supprimer définitivement ${sigSel.length} signal(aux) ?`)) return;
    if (action === "exclude_company" && !window.confirm(`Exclure des futurs signaux les entreprises de ${sigSel.length} signal(aux) sélectionné(s) ?`)) return;
    setBulkRowBusy(true);
    try {
      const { data } = await api.post("/admin/signals/bulk", { ids: sigSel, action, value });
      const msg = action === "delete" ? `${data.deleted} signal(aux) supprimé(s)`
        : action === "exclude_company" ? `${data.companies_excluded} entreprise(s) exclue(s)`
        : `${data.updated} signal(aux) mis à jour`;
      toast.success(msg); setSigSel([]); refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBulkRowBusy(false); }
  };

  // Sélection en masse — Contacts
  const toggleCt = (id) => setCtSel((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const toggleAllCt = () => setCtSel((p) => (p.length === contacts.length ? [] : contacts.map((c) => c.id)));
  const runCtBulk = async (action, value) => {
    if (!ctSel.length) return;
    if (action === "delete" && !window.confirm(`Supprimer définitivement ${ctSel.length} contact(s) ?`)) return;
    setBulkRowBusy(true);
    try {
      const { data } = await api.post("/admin/contacts/bulk", { ids: ctSel, action, value });
      const msg = action === "delete" ? `${data.deleted} contact(s) supprimé(s)` : `${data.updated} contact(s) mis à jour`;
      toast.success(msg); setCtSel([]); refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBulkRowBusy(false); }
  };

  // Chargements globaux (une fois).
  useEffect(() => {
    loadCounts(); loadOptions(); loadGate(); loadEsnVis(); loadIncompleteCount(); loadDomainFailedCount(); loadExcludedCount();
  }, [loadCounts, loadOptions, loadGate, loadEsnVis, loadIncompleteCount, loadDomainFailedCount, loadExcludedCount]);

  // (Re)charge l'onglet actif à chaque changement d'onglet / recherche / filtre / tri / page (debounce).
  useEffect(() => {
    const delay = firstRender.current ? 0 : 250;
    firstRender.current = false;
    const h = setTimeout(() => { fetchTab(tab, query.trim()).catch(() => {}); }, delay);
    return () => clearTimeout(h);
  }, [fetchTab, tab, query]);

  // Revenir en page 1 + vider les sélections quand un filtre/tri/onglet/recherche/taille change.
  useEffect(() => {
    setPage(1); setSigSel([]); setCtSel([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, query, pageSize, sort, order, missionFilter, providerFilter, sigCompany, sigCity, sigClientType, coCity, coType, coSector, ctCompany, ctCity, ctSeniority, ctHasEmail]);

  const downloadCsv = async (kind) => {
    setDl(true);
    try {
      const res = await api.get(`/admin/export/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `interko_${kind}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export CSV téléchargé");
    } catch { toast.error("Export impossible"); } finally { setDl(false); }
  };

  const updateClientType = async (c, v) => {
    setCompanies((prev) => prev.map((x) => (x.id === c.id ? { ...x, client_type: v } : x)));
    try {
      await api.patch(`/admin/companies/${c.id}`, { client_type: v });
      toast.success(`${c.name} → ${v === "intermediary" ? "ESN" : "Client Final"}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); refresh(); }
  };

  const CLASS_LABELS = {
    FINAL_CLIENT: "Client final", ESN_CONSULTING: "ESN / Cabinet",
    STAFFING_RECRUITMENT: "Intermédiaire", UNKNOWN: "À confirmer",
  };
  const updateClass = async (c, v) => {
    setCompanies((prev) => prev.map((x) => (x.id === c.id ? { ...x, company_class: v, class_manual: true, class_confidence: 100 } : x)));
    try {
      await api.patch(`/admin/companies/${c.id}`, { company_class: v });
      toast.success(`${c.name} → ${CLASS_LABELS[v]} (correction manuelle)`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); refresh(); }
  };

  const enrichDomain = async (companyId, companyName, method = "web") => {
    setEnriching(companyId);
    try {
      const { data } = await api.post(`/admin/companies/${companyId}/enrich-domain`, null, { params: { method } });
      if (data.error && !data.not_found) toast.error(`Enrichissement : ${data.error}`);
      else if (data.found) toast.success(`Domaine trouvé pour ${companyName} : ${data.domain}`);
      else if (data.not_found) toast.warning(`Aucun domaine trouvé pour ${companyName} (web + Prospeo) → déplacée dans « Domaine introuvable », signaux désactivés côté ESN.`);
      else toast.info(`Aucun domaine trouvé pour ${companyName} (${method === "prospeo" ? "Prospeo" : "web"}).`);
      await fetchTab(tab, query.trim());
      loadIncompleteCount(); loadGate();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setEnriching(null); }
  };

  // A2 — Fusion d'entreprises
  const toggleMergeSel = (id) => {
    setMergeSel((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      if (mergeTarget && !next.includes(mergeTarget)) setMergeTarget("");
      if (!mergeTarget && next.length) setMergeTarget(next[0]);
      return next;
    });
  };
  const cancelMerge = () => { setMergeSel([]); setMergeTarget(""); };
  const runMerge = async () => {
    if (mergeSel.length < 2 || !mergeTarget) { toast.error("Sélectionnez au moins 2 entreprises et une cible."); return; }
    const sources = mergeSel.filter((x) => x !== mergeTarget);
    const targetName = companies.find((c) => c.id === mergeTarget)?.name || "cible";
    if (!window.confirm(`Fusionner ${sources.length} entreprise(s) dans « ${targetName} » ? Tous leurs signaux, contacts et implantations seront rattachés à « ${targetName} », puis les doublons seront supprimés.`)) return;
    setMerging(true);
    try {
      const { data } = await api.post("/admin/companies/merge", { target_id: mergeTarget, source_ids: sources });
      toast.success(`Fusion réussie → « ${data.target?.name}» · ${data.signals_moved} signal(aux), ${data.contacts_moved} contact(s), ${data.offices_moved} implantation(s) rattachés.`);
      cancelMerge();
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setMerging(false); }
  };

  const openExclude = (company) => {
    setExcludeTarget(company);
    setExcludeForm({ reason: "", note: "", cleanup: "keep" });
  };
  const runExclude = async () => {
    if (!excludeTarget) return;
    setExcludeBusy(true);
    try {
      const { data } = await api.post(`/admin/companies/${excludeTarget.id}/exclude-from-signals`, {
        reason: excludeForm.reason, note: excludeForm.note, cleanup: excludeForm.cleanup,
      });
      const extra = data.archived ? ` · ${data.archived} signal(aux) archivé(s)` : data.deleted ? ` · ${data.deleted} signal(aux) supprimé(s)` : "";
      toast.success(`« ${excludeTarget.name} » exclue des signaux${extra}`);
      setExcludeTarget(null);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setExcludeBusy(false); }
  };
  const allowSignals = async (company) => {
    if (!window.confirm(`Réautoriser « ${company.name} » ? Ses futurs signaux seront de nouveau collectés.`)) return;
    try {
      const { data } = await api.post(`/admin/companies/${company.id}/allow-signals`);
      const extra = data.restored ? ` · ${data.restored} signal(aux) réactivé(s)` : "";
      toast.success(`« ${company.name} » réautorisée${extra}`);
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleEsnHidden = async (s) => {
    try {
      await api.patch(`/admin/signals/${s.id}`, { esn_hidden: !s.esn_hidden });
      setSignals((prev) => prev.map((x) => (x.id === s.id ? { ...x, esn_hidden: !x.esn_hidden } : x)));
      loadEsnVis();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleMissionVis = async (type) => {
    const cur = new Set(esnVis?.hidden_types || []);
    cur.has(type) ? cur.delete(type) : cur.add(type);
    try {
      const { data } = await api.post("/admin/esn-visibility/mission-types", { types: [...cur] });
      setEsnVis(data);
      toast.success(data.hidden_types.length ? `Masqués aux ESN : ${data.hidden_types.join(", ")}` : "Aucun type masqué aux ESN");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const runBulk = async () => {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/domains/enrich-bulk", { limit: 50 });
      toast.success(`Enrichissement web lancé sur ${data.planned} entreprise(s) — en arrière-plan.`);
      setTimeout(() => { fetchTab("incomplete", query.trim()); loadIncompleteCount(); loadGate(); }, 12000);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBulkBusy(false); }
  };

  // Debounced server-side search on the active tab.

  const openCreate = () => {
    setEditing(null);
    if (tab === "companies") setForm({ name: "", sector: "", size: "", city: "", region: "", lat: "", lng: "", website: "", logo_color: "#00E5FF", description: "" });
    if (tab === "signals") setForm({ company_id: "", company_name: "", role_title: "", skills: "", seniority: "Confirmé", contract_type: "Régie", client_type: "end_client", description: "", mission_types: ["Régie"], esn_hidden: false, city: "", region: "", office_id: "" });
    if (tab === "contacts") setForm({ company_id: "", company_name: "", name: "", title: "", seniority: "Décideur", email: "", phone: "", linkedin: "", city: "", region: "" });
    setFormOffices([]);
    setDialogOpen(true);
  };

  // Charge les implantations (agences) de l'entreprise pour le sélecteur "Agence" du formulaire signal.
  const loadFormOffices = useCallback(async (companyId) => {
    if (!companyId) { setFormOffices([]); return; }
    setFormOfficesLoading(true);
    try {
      const { data } = await api.get(`/admin/companies/${companyId}/offices`);
      setFormOffices(data.offices || []);
    } catch { setFormOffices([]); }
    finally { setFormOfficesLoading(false); }
  }, []);

  // Sélection d'entreprise depuis le combobox (signaux / contacts).
  const selectCompany = (id, name, comp) => {
    setForm((f) => ({
      ...f, company_id: id, company_name: name,
      // pré-remplit la ville/région depuis l'entreprise si les champs sont vides
      city: f.city || comp?.city || "",
      region: f.region || comp?.region || "",
      // changer d'entreprise réinitialise l'agence (les agences sont propres à l'entreprise)
      office_id: "",
      // le type de client du signal suit celui de l'entreprise choisie
      ...(comp?.client_type ? { client_type: comp.client_type } : {}),
    }));
    if (tab === "signals") loadFormOffices(id);
  };

  // Onglet Entreprises : sélectionner une entreprise existante bascule en édition (évite les doublons).
  const pickExistingCompany = async (id) => {
    if (!id) return;
    try {
      const { data } = await api.get(`/companies/${id}`);
      const c = data.company;
      setEditing(c);
      setForm({ ...c });
      toast.info(`Modification de « ${c.name} »`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openEdit = (row) => {
    setEditing(row);
    if (tab === "signals") {
      setForm({ ...row, skills: (row.skills || []).join(", "), mission_types: row.mission_types || [], esn_hidden: !!row.esn_hidden, office_id: row.office_id || "" });
      loadFormOffices(row.company_id);
    }
    else setForm({ ...row });
    setDialogOpen(true);
  };

  const save = async () => {
    const base = tab === "domain-failed" ? "/admin/companies" : `/admin/${tab}`;
    if (tab === "signals") {
      if (!form.company_id) { toast.error("Sélectionnez une entreprise"); return; }
      if (!(form.role_title || "").trim()) { toast.error("L'intitulé du poste est requis"); return; }
      if (!(form.city || "").trim()) { toast.error("Le lieu de la mission est requis (indispensable à la recherche géographique ESN)"); return; }
    }
    try {
      if (editing) await api.patch(`${base}/${editing.id}`, form);
      else await api.post(base, form);
      toast.success(editing ? "Modifié" : "Ajouté");
      setDialogOpen(false);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    const base = tab === "domain-failed" ? "/admin/companies" : `/admin/${tab}`;
    try {
      await api.delete(`${base}/${id}`);
      toast.success("Supprimé");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const onCsvFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setCsvText(String(reader.result || ""));
    reader.readAsText(file);
  };

  const runImport = async () => {
    if (!csvText.trim()) { toast.error("Collez ou importez un fichier CSV"); return; }
    setImporting(true);
    try {
      const { data } = await api.post(`/admin/import/${tab}`, { csv: csvText });
      const extra = [
        data.updated ? `${data.updated} maj` : "",
        data.companies_created ? `${data.companies_created} entreprise(s) créée(s)` : "",
        data.errors?.length ? `${data.errors.length} erreur(s)` : "",
      ].filter(Boolean).join(" · ");
      toast.success(`${data.created} ligne(s) importée(s)${extra ? ` · ${extra}` : ""}`);
      if (data.errors?.length) console.warn("Import errors:", data.errors);
      setImportOpen(false);
      setCsvText("");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setImporting(false);
    }
  };

  const exportable = tab === "companies" || tab === "signals" || tab === "contacts";
  const importable = tab === "companies" || tab === "signals" || tab === "contacts";
  const csvHint = tab === "companies"
    ? "Colonnes : name,sector,size,city,region,lat,lng,website,logo_color,description"
    : tab === "contacts"
    ? "Colonnes : name,title,email,phone,linkedin,company_name,company_domain,category (BESOIN/DÉCISION/ACHAT),seniority,verified"
    : "Colonnes : company_name,role_title,skills,seniority,contract_type,description (skills séparées par des virgules ou |)";

  const rows = { companies, signals, contacts }[tab];

  return (
    <div data-testid="admin-content">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="overline mb-2">Contenu marché</div>
          <h1 className="font-display text-3xl font-bold">Entreprises, signaux & contacts</h1>
        </div>
        <div className="flex items-center gap-3">
          {(tab === "companies" || tab === "contacts") && (
            <button onClick={openDedup} data-testid="content-dedup-btn"
              className="flex items-center gap-2 border border-amber-500/40 text-amber-300 font-semibold px-4 py-2.5 rounded-md hover-lift hover:border-amber-400/60">
              <Broom size={16} /> Nettoyer les doublons
            </button>
          )}
          {exportable && (
            <button onClick={() => downloadCsv(tab)} disabled={dl} data-testid="content-export-btn"
              className="flex items-center gap-2 border border-border text-fg font-semibold px-4 py-2.5 rounded-md hover-lift hover:border-accent/40 disabled:opacity-60">
              <DownloadSimple size={16} /> {dl ? "Export…" : "Télécharger CSV"}
            </button>
          )}
          {importable && (
            <button onClick={() => { if (tab === "signals") { setWizardOpen(true); } else if (tab === "contacts") { setContactWizardOpen(true); } else if (tab === "companies") { setCompanyWizardOpen(true); } else { setCsvText(""); setImportOpen(true); } }} data-testid="content-import-btn"
              className="flex items-center gap-2 border border-border text-fg font-semibold px-4 py-2.5 rounded-md hover-lift hover:border-accent/40">
              <UploadSimple size={16} /> Importer CSV
            </button>
          )}
          {tab !== "incomplete" && tab !== "domain-failed" && tab !== "excluded" && (
            <button onClick={openCreate} data-testid="content-add-btn"
              className="flex items-center gap-2 bg-accent text-black font-semibold px-4 py-2.5 rounded-md hover-lift">
              <Plus size={16} weight="bold" /> Ajouter
            </button>
          )}
        </div>
      </div>

      <Tabs value={tab} onValueChange={changeTab}>
        <TabsList className="bg-surface border border-border mb-5 p-1 gap-1">
          <TabsTrigger value="companies" data-testid="tab-companies" className="data-[state=active]:bg-accent data-[state=active]:text-black data-[state=active]:font-bold text-muted px-4">Entreprises ({counts.companies})</TabsTrigger>
          <TabsTrigger value="signals" data-testid="tab-signals" className="data-[state=active]:bg-accent data-[state=active]:text-black data-[state=active]:font-bold text-muted px-4">Signaux ({counts.signals})</TabsTrigger>
          <TabsTrigger value="contacts" data-testid="tab-contacts" className="data-[state=active]:bg-accent data-[state=active]:text-black data-[state=active]:font-bold text-muted px-4">Contacts ({counts.contacts})</TabsTrigger>
          <TabsTrigger value="incomplete" data-testid="tab-incomplete">À compléter{incompleteCount ? ` (${incompleteCount})` : ""}</TabsTrigger>
          <TabsTrigger value="domain-failed" data-testid="tab-domain-failed">Domaine introuvable{domainFailedCount ? ` (${domainFailedCount})` : ""}</TabsTrigger>
          <TabsTrigger value="excluded" data-testid="tab-excluded">Entreprises exclues{excludedCount ? ` (${excludedCount})` : ""}</TabsTrigger>
        </TabsList>

        <div className="relative mb-4 max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="content-search"
            placeholder={tab === "companies" ? "Rechercher une entreprise (nom, ville, secteur)…" : tab === "contacts" ? "Rechercher un contact (nom, fonction, email, entreprise)…" : "Rechercher un signal (intitulé, ville, compétence, entreprise)…"}
            className="bg-surface-2 border-border text-fg pl-9" />
        </div>

        {/* Companies */}
        <TabsContent value="companies">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3" data-testid="merge-toolbar">
            <p className="text-xs text-muted">Cochez plusieurs entreprises pour les <strong className="text-fg">fusionner</strong> sous un seul nom (= siège). Tous leurs signaux/contacts/implantations seront rattachés à la cible.</p>
            {mergeSel.length >= 1 && (
              <div className="flex flex-wrap items-center gap-2 surface border border-accent/30 rounded-md px-3 py-2" data-testid="merge-bar">
                <span className="text-xs font-mono text-accent">{mergeSel.length} sélectionnée(s)</span>
                <span className="text-xs text-muted">Cible (siège) :</span>
                <Select value={mergeTarget} onValueChange={setMergeTarget}>
                  <SelectTrigger className={inputCls + " h-8 w-48 text-xs"} data-testid="merge-target-select"><SelectValue placeholder="Choisir l'entreprise cible" /></SelectTrigger>
                  <SelectContent>
                    {mergeSel.map((id) => {
                      const c = companies.find((x) => x.id === id);
                      return <SelectItem key={id} value={id}>{c?.name || id}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
                <button onClick={runMerge} disabled={merging || mergeSel.length < 2 || !mergeTarget} data-testid="merge-confirm-btn"
                  className="flex items-center gap-1.5 bg-accent text-black font-semibold text-xs px-3 py-1.5 rounded-md hover-lift disabled:opacity-50">
                  <Buildings size={13} weight="bold" /> {merging ? "Fusion…" : "Fusionner"}
                </button>
                <button onClick={cancelMerge} data-testid="merge-cancel-btn" className="text-xs text-muted hover:text-fg px-2 py-1.5">Annuler</button>
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-end gap-2 mb-3" data-testid="companies-filters">
            <div className="w-40"><label className="overline !text-muted block mb-1">Ville</label>
              <Input value={coCity} onChange={(e) => setCoCity(e.target.value)} placeholder="Ville…" className={inputCls + " h-9 text-xs"} data-testid="co-filter-city" /></div>
            <div className="w-44"><label className="overline !text-muted block mb-1">Classification</label>
              <Select value={coType} onValueChange={setCoType}>
                <SelectTrigger className={inputCls + " h-9 text-xs"} data-testid="co-filter-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes</SelectItem>
                  <SelectItem value="FINAL_CLIENT">Client final</SelectItem>
                  <SelectItem value="ESN_CONSULTING">ESN / Cabinet</SelectItem>
                  <SelectItem value="STAFFING_RECRUITMENT">Intermédiaire</SelectItem>
                  <SelectItem value="UNKNOWN">À confirmer</SelectItem>
                </SelectContent>
              </Select></div>
            <div className="w-40"><label className="overline !text-muted block mb-1">Secteur</label>
              <Input value={coSector} onChange={(e) => setCoSector(e.target.value)} placeholder="Secteur…" className={inputCls + " h-9 text-xs"} data-testid="co-filter-sector" /></div>
          </div>
          <Table sort={sort} order={order} onSort={toggleSort}
            headers={["", { label: "Nom", field: "name" }, "SIREN", { label: "Classification", field: "company_class" }, { label: "Secteur", field: "sector" }, { label: "Ville", field: "city" }, "Signaux", "Contacts", ""]}>
            {companies.map((c) => (
              <Row key={c.id} testid={`company-row-${c.id}`} onEdit={() => openEdit(c)} onDelete={() => remove(c.id)}
                extraActions={
                  <>
                    {c.signalPolicy === "EXCLUDE" ? (
                      <button onClick={() => allowSignals(c)} title="Réautoriser les signaux"
                        className="p-2 rounded-md text-emerald-400 hover:bg-surface-2" data-testid={`allow-company-${c.id}`}>
                        <ArrowCounterClockwise size={15} />
                      </button>
                    ) : (
                      <button onClick={() => openExclude(c)} title="Exclure des signaux"
                        className="p-2 rounded-md text-muted hover:text-amber-400 hover:bg-surface-2" data-testid={`exclude-company-${c.id}`}>
                        <Prohibit size={15} />
                      </button>
                    )}
                    <button onClick={() => setOfficesFor(c)} title="Implantations"
                      className="p-2 rounded-md text-muted hover:text-accent hover:bg-surface-2" data-testid={`manage-offices-${c.id}`}>
                      <MapPin size={15} />
                    </button>
                  </>
                }>
                <td className="px-4 py-3">
                  <input type="checkbox" checked={mergeSel.includes(c.id)} onChange={() => toggleMergeSel(c.id)}
                    className="accent-[#00E5FF] w-4 h-4 cursor-pointer" data-testid={`merge-check-${c.id}`} />
                </td>
                <td className="px-4 py-3 text-fg font-medium">
                  <span className="flex items-center gap-2">
                    {c.name}
                    {c.signalPolicy === "EXCLUDE" && (
                      <span className="text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded bg-amber-500/12 text-amber-300 border border-amber-500/30 whitespace-nowrap"
                        data-testid={`company-excluded-badge-${c.id}`} title="Exclue des signaux">Exclue</span>
                    )}
                  </span>
                </td>
                <td className="px-4 py-3" data-testid={`company-siren-${c.id}`}>
                  {c.siren
                    ? <span className="font-mono text-xs text-emerald-300">{c.siren}</span>
                    : <span className="font-mono text-xs text-muted">—</span>}
                </td>
                <td className="px-4 py-3" data-testid={`company-type-${c.id}`}>
                  <Select value={c.company_class || (c.client_type === "intermediary" ? "ESN_CONSULTING" : "FINAL_CLIENT")} onValueChange={(v) => updateClass(c, v)}>
                    <SelectTrigger className={inputCls + " h-8 w-36 text-xs"} data-testid={`company-class-select-${c.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="FINAL_CLIENT">Client final</SelectItem>
                      <SelectItem value="ESN_CONSULTING">ESN / Cabinet</SelectItem>
                      <SelectItem value="STAFFING_RECRUITMENT">Intermédiaire</SelectItem>
                      <SelectItem value="UNKNOWN">À confirmer</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="mt-1 flex items-center gap-1 text-[10px] font-mono text-muted" title={(c.class_reasons || []).join(" · ")}>
                    {c.class_manual ? <span className="text-cyan-300">manuel</span>
                      : <span>{c.class_confidence != null ? `conf. ${c.class_confidence}%` : "—"}{c.class_source ? ` · ${c.class_source}` : ""}</span>}
                  </div>
                </td>
                <td className="px-4 py-3 text-muted">{c.sector}</td>
                <td className="px-4 py-3 text-muted">{c.city}</td>
                <td className="px-4 py-3 font-mono text-muted">{c.signals_count}</td>
                <td className="px-4 py-3 font-mono text-muted">{c.contacts_count}</td>
              </Row>
            ))}
          </Table>
          <Paginator page={page} total={activeTotal} pageSize={pageSize} onPage={setPage} onPageSize={setPageSize} />
        </TabsContent>

        {/* Signals */}
        <TabsContent value="signals">
          <div className="surface rounded-lg p-4 mb-4 space-y-3" data-testid="esn-visibility-panel">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="overline !text-fg/70 flex items-center gap-1.5"><Eye size={13} className="text-accent" /> Visibilité ESN par type de mission</div>
                <p className="text-[11px] text-muted mt-1 max-w-2xl">
                  Cliquez un type pour le <b>masquer</b> aux ESN (recherche). Le champ « Type Mission » reste réservé à l'admin.
                  {esnVis?.none_count ? ` · ${esnVis.none_count} signaux sans type détecté (« Non précisé »).` : ""}
                </p>
              </div>
              <div className="flex items-end gap-2 flex-wrap">
                <div className="w-40">
                  <label className="overline !text-muted block mb-1">Entreprise</label>
                  <Input value={sigCompany} onChange={(e) => setSigCompany(e.target.value)} placeholder="Entreprise…" className={inputCls + " h-9 text-xs"} data-testid="sig-filter-company" />
                </div>
                <div className="w-32">
                  <label className="overline !text-muted block mb-1">Ville</label>
                  <Input value={sigCity} onChange={(e) => setSigCity(e.target.value)} placeholder="Ville…" className={inputCls + " h-9 text-xs"} data-testid="sig-filter-city" />
                </div>
                <div className="w-40">
                  <label className="overline !text-muted block mb-1">ESN / Client</label>
                  <Select value={sigClientType} onValueChange={setSigClientType}>
                    <SelectTrigger className={inputCls + " h-9 text-xs"} data-testid="sig-filter-clienttype"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous</SelectItem>
                      <SelectItem value="end_client">Client Final</SelectItem>
                      <SelectItem value="intermediary">ESN</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-44">
                  <label className="overline !text-muted block mb-1">Provenance</label>
                  <Select value={providerFilter} onValueChange={setProviderFilter}>
                    <SelectTrigger className={inputCls + " h-9"} data-testid="provider-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes les sources</SelectItem>
                      <SelectItem value="JSEARCH">JSearch</SelectItem>
                      <SelectItem value="THEIRSTACK">TheirStack</SelectItem>
                      <SelectItem value="FANTASTIC_JOBS">Fantastic.jobs</SelectItem>
                      <SelectItem value="FRANCE_TRAVAIL">France Travail</SelectItem>
                      <SelectItem value="HASDATA">Indeed (HasData)</SelectItem>
                      <SelectItem value="SERPAPI">Google Jobs (SerpApi)</SelectItem>
                      <SelectItem value="ADZUNA">Adzuna</SelectItem>
                      <SelectItem value="LEGACY">Import</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-52">
                  <label className="overline !text-muted block mb-1">Type de mission</label>
                  <Select value={missionFilter} onValueChange={setMissionFilter}>
                    <SelectTrigger className={inputCls + " h-9"} data-testid="mission-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les types</SelectItem>
                      {MISSION_TYPES.map((t) => <SelectItem key={t} value={t}>{t} ({esnVis?.counts_by_type?.[t] || 0})</SelectItem>)}
                      <SelectItem value="__none__">Non précisé ({esnVis?.none_count || 0})</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {MISSION_TYPES.map((t) => {
                const hidden = (esnVis?.hidden_types || []).includes(t);
                return (
                  <button key={t} onClick={() => toggleMissionVis(t)} data-testid={`mission-vis-${t}`}
                    className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md border ${hidden ? "bg-red-500/10 border-red-500/40 text-red-300" : "bg-surface-2 border-border text-fg/80 hover:border-accent/40"}`}>
                    {hidden ? <EyeSlash size={13} /> : <Eye size={13} />} {t}
                    <span className="font-mono text-[10px] text-muted">{esnVis?.counts_by_type?.[t] || 0}</span>
                  </button>
                );
              })}
            </div>
            <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-border/60">
              <span className="overline !text-muted mr-1">Actions rapides</span>
              <button onClick={() => setMissionFilter(missionFilter === "Client final" ? "all" : "Client final")}
                data-testid="quick-final-clients"
                className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border font-medium ${missionFilter === "Client final" ? "bg-accent/15 border-accent/50 text-accent" : "bg-surface-2 border-border text-fg/80 hover:border-accent/40"}`}>
                <Buildings size={14} /> {missionFilter === "Client final" ? "Clients finaux uniquement ✓" : "Clients finaux uniquement"}
              </button>
              {(() => {
                const interHidden = (esnVis?.hidden_types || []).includes("Intermédiaire");
                return (
                  <button onClick={() => toggleMissionVis("Intermédiaire")} data-testid="quick-hide-intermediaries"
                    className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border font-medium ${interHidden ? "bg-emerald-500/12 border-emerald-500/40 text-emerald-300" : "bg-surface-2 border-border text-fg/80 hover:border-accent/40"}`}>
                    {interHidden ? <><CheckCircle size={14} weight="fill" /> Seuls les clients finaux visibles par les ESN</> : <><EyeSlash size={14} /> Masquer les intermédiaires aux ESN</>}
                  </button>
                );
              })()}
            </div>
          </div>
          {sigSel.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 surface border border-accent/30 rounded-md px-3 py-2 mb-3" data-testid="signals-bulk-bar">
              <span className="text-xs font-mono text-accent">{sigSel.length} sélectionné(s)</span>
              <button onClick={() => runSigBulk("esn_hidden", false)} disabled={bulkRowBusy} data-testid="sig-bulk-show" className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border border-border text-emerald-400 hover:border-accent/40 disabled:opacity-50"><Eye size={13} /> Afficher aux ESN</button>
              <button onClick={() => runSigBulk("esn_hidden", true)} disabled={bulkRowBusy} data-testid="sig-bulk-hide" className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border border-border text-red-400 hover:border-accent/40 disabled:opacity-50"><EyeSlash size={13} /> Masquer aux ESN</button>
              <div className="flex items-center gap-1">
                <Select value={bulkSigType} onValueChange={setBulkSigType}>
                  <SelectTrigger className={inputCls + " h-8 w-40 text-xs"} data-testid="sig-bulk-mission-select"><SelectValue placeholder="Type de mission…" /></SelectTrigger>
                  <SelectContent>{MISSION_TYPE_OPTIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
                <button onClick={() => bulkSigType && runSigBulk("mission_type", bulkSigType)} disabled={bulkRowBusy || !bulkSigType} data-testid="sig-bulk-mission-apply" className="text-xs px-2.5 py-1.5 rounded-md border border-border text-fg hover:border-accent/40 disabled:opacity-50">Appliquer</button>
              </div>
              <button onClick={() => runSigBulk("exclude_company")} disabled={bulkRowBusy} data-testid="sig-bulk-exclude" className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border border-amber-500/40 text-amber-300 hover:border-amber-400/60 disabled:opacity-50"><Prohibit size={13} /> Exclure l'entreprise</button>
              <button onClick={() => runSigBulk("delete")} disabled={bulkRowBusy} data-testid="sig-bulk-delete" className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border border-destructive/40 text-destructive hover:bg-destructive/10 disabled:opacity-50"><Trash size={13} /> Supprimer</button>
              <button onClick={() => setSigSel([])} className="text-xs text-muted hover:text-fg px-2 py-1.5">Annuler</button>
            </div>
          )}
          <Table sort={sort} order={order} onSort={toggleSort}
            headers={[
              { node: <input type="checkbox" aria-label="Tout sélectionner" checked={signals.length > 0 && sigSel.length === signals.length} onChange={toggleAllSig} className="accent-[#00E5FF] w-4 h-4 cursor-pointer" data-testid="sig-select-all" /> },
              { label: "Intitulé", field: "role_title" }, "Entreprise", { label: "Ville", field: "city" },
              { label: "Type Mission", field: "mission_types" }, "Provenance", { label: "Date pub.", field: "posted_at" },
              { label: "Type", field: "client_type" }, "Visible ESN", ""]}>
            {signals.length === 0 && (
              <tr><td colSpan={10} className="px-4 py-8 text-center text-muted text-sm">Aucun signal.</td></tr>
            )}
            {signals.map((s) => (
              <Row key={s.id} testid={`signal-row-${s.id}`} onEdit={() => openEdit(s)} onDelete={() => remove(s.id)}
                extraActions={
                  <button onClick={() => openExclude({ id: s.company_id, name: s.company_name })} title="Exclure l'entreprise des signaux"
                    className="p-2 rounded-md text-muted hover:text-amber-400 hover:bg-surface-2" data-testid={`signal-exclude-company-${s.id}`}>
                    <Prohibit size={15} />
                  </button>
                }>
                <td className="px-4 py-3"><input type="checkbox" checked={sigSel.includes(s.id)} onChange={() => toggleSig(s.id)} className="accent-[#00E5FF] w-4 h-4 cursor-pointer" data-testid={`sig-check-${s.id}`} /></td>
                <td className="px-4 py-3 text-fg font-medium">{s.role_title}</td>
                <td className="px-4 py-3 text-muted">{s.company_name}</td>
                <td className="px-4 py-3 text-muted whitespace-nowrap" data-testid={`signal-city-${s.id}`}>{s.city || "—"}</td>
                <td className="px-4 py-3" data-testid={`signal-mission-${s.id}`}>
                  {(s.mission_types || []).length ? (
                    <span className="flex flex-wrap gap-1">
                      {(s.mission_types || []).map((m) => <span key={m} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-accent/12 text-accent">{m}</span>)}
                    </span>
                  ) : <span className="text-xs text-muted">Non précisé</span>}
                </td>
                <td className="px-4 py-3" data-testid={`signal-provenance-${s.id}`}>
                  {(s.providers || []).length ? (
                    <span className="flex flex-wrap gap-1" title={s.origin_source ? `Origine : ${s.origin_source}` : undefined}>
                      {(s.providers || []).map((p) => (
                        <span key={p} className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-sky-500/12 text-sky-300 border border-sky-500/25 whitespace-nowrap">
                          {providerLabel(p)}
                        </span>
                      ))}
                    </span>
                  ) : s.origin_source ? (
                    <span className="text-[10px] font-mono text-muted">{s.origin_source}</span>
                  ) : <span className="text-xs text-muted">—</span>}
                </td>
                <td className="px-4 py-3 text-muted whitespace-nowrap font-mono text-xs" data-testid={`signal-date-${s.id}`}>{fmtSignalDate(s.posted_at || s.first_seen_at)}</td>
                <td className="px-4 py-3" data-testid={`signal-clienttype-${s.id}`}>
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border whitespace-nowrap ${s.client_type === "intermediary" ? "bg-purple-500/12 text-purple-300 border-purple-500/30" : "bg-emerald-500/12 text-emerald-300 border-emerald-500/30"}`}>{s.client_type === "intermediary" ? "ESN" : "Client Final"}</span>
                </td>
                <td className="px-4 py-3">
                  <button onClick={() => toggleEsnHidden(s)} data-testid={`signal-esn-toggle-${s.id}`}
                    title={s.esn_hidden ? "Masqué aux ESN — cliquer pour rendre visible" : "Visible par les ESN — cliquer pour masquer"}
                    className={`p-1.5 rounded-md ${s.esn_hidden ? "text-red-400 hover:bg-red-500/10" : "text-emerald-400 hover:bg-surface-2"}`}>
                    {s.esn_hidden ? <EyeSlash size={16} /> : <Eye size={16} />}
                  </button>
                </td>
              </Row>
            ))}
          </Table>
          <Paginator page={page} total={activeTotal} pageSize={pageSize} onPage={setPage} onPageSize={setPageSize} />
        </TabsContent>

        {/* Contacts */}
        <TabsContent value="contacts">
          <EmailPatternStats />
          <div className="flex flex-wrap items-end gap-2 mb-3" data-testid="contacts-filters">
            <div className="w-44"><label className="overline !text-muted block mb-1">Entreprise</label>
              <Input value={ctCompany} onChange={(e) => setCtCompany(e.target.value)} placeholder="Entreprise…" className={inputCls + " h-9 text-xs"} data-testid="ct-filter-company" /></div>
            <div className="w-36"><label className="overline !text-muted block mb-1">Ville</label>
              <Input value={ctCity} onChange={(e) => setCtCity(e.target.value)} placeholder="Ville…" className={inputCls + " h-9 text-xs"} data-testid="ct-filter-city" /></div>
            <div className="w-40"><label className="overline !text-muted block mb-1">Type</label>
              <Select value={ctSeniority} onValueChange={setCtSeniority}>
                <SelectTrigger className={inputCls + " h-9 text-xs"} data-testid="ct-filter-seniority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="Décideur">Décideur</SelectItem>
                  <SelectItem value="Opérationnel">Opérationnel</SelectItem>
                </SelectContent>
              </Select></div>
            <div className="w-40"><label className="overline !text-muted block mb-1">Email</label>
              <Select value={ctHasEmail} onValueChange={setCtHasEmail}>
                <SelectTrigger className={inputCls + " h-9 text-xs"} data-testid="ct-filter-email"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="true">Avec email</SelectItem>
                  <SelectItem value="false">Sans email</SelectItem>
                </SelectContent>
              </Select></div>
          </div>
          {ctSel.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 surface border border-accent/30 rounded-md px-3 py-2 mb-3" data-testid="contacts-bulk-bar">
              <span className="text-xs font-mono text-accent">{ctSel.length} sélectionné(s)</span>
              <button onClick={() => runCtBulk("verified", true)} disabled={bulkRowBusy} data-testid="ct-bulk-verify" className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border border-border text-emerald-400 hover:border-accent/40 disabled:opacity-50"><CheckCircle size={13} /> Marquer vérifié</button>
              <button onClick={() => runCtBulk("verified", false)} disabled={bulkRowBusy} data-testid="ct-bulk-unverify" className="text-xs px-2.5 py-1.5 rounded-md border border-border text-muted hover:border-accent/40 disabled:opacity-50">Non vérifié</button>
              <div className="flex items-center gap-1">
                <Select value={bulkCtType} onValueChange={setBulkCtType}>
                  <SelectTrigger className={inputCls + " h-8 w-40 text-xs"} data-testid="ct-bulk-type-select"><SelectValue placeholder="Type…" /></SelectTrigger>
                  <SelectContent>{CONTACT_SENIORITY.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
                <button onClick={() => bulkCtType && runCtBulk("seniority", bulkCtType)} disabled={bulkRowBusy || !bulkCtType} data-testid="ct-bulk-type-apply" className="text-xs px-2.5 py-1.5 rounded-md border border-border text-fg hover:border-accent/40 disabled:opacity-50">Appliquer</button>
              </div>
              <button onClick={() => runCtBulk("delete")} disabled={bulkRowBusy} data-testid="ct-bulk-delete" className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border border-destructive/40 text-destructive hover:bg-destructive/10 disabled:opacity-50"><Trash size={13} /> Supprimer</button>
              <button onClick={() => setCtSel([])} className="text-xs text-muted hover:text-fg px-2 py-1.5">Annuler</button>
            </div>
          )}
          <Table sort={sort} order={order} onSort={toggleSort}
            headers={[
              { node: <input type="checkbox" aria-label="Tout sélectionner" checked={contacts.length > 0 && ctSel.length === contacts.length} onChange={toggleAllCt} className="accent-[#00E5FF] w-4 h-4 cursor-pointer" data-testid="ct-select-all" /> },
              { label: "Nom", field: "name" }, "Entreprise", { label: "Fonction", field: "title" }, { label: "Ville / Région", field: "city" }, { label: "Type", field: "seniority" }, { label: "Email", field: "email" }, ""]}>
            {contacts.length === 0 && (<tr><td colSpan={8} className="px-4 py-8 text-center text-muted text-sm">Aucun contact.</td></tr>)}
            {contacts.map((c) => (
              <Row key={c.id} testid={`contact-row-${c.id}`} onEdit={() => openEdit(c)} onDelete={() => remove(c.id)}>
                <td className="px-4 py-3"><input type="checkbox" checked={ctSel.includes(c.id)} onChange={() => toggleCt(c.id)} className="accent-[#00E5FF] w-4 h-4 cursor-pointer" data-testid={`ct-check-${c.id}`} /></td>
                <td className="px-4 py-3 text-fg font-medium">{c.name}</td>
                <td className="px-4 py-3 text-muted">{c.company_name}</td>
                <td className="px-4 py-3 text-muted">{c.title}</td>
                <td className="px-4 py-3 text-muted whitespace-nowrap" data-testid={`contact-geo-${c.id}`}>{[c.city, c.region].filter(Boolean).join(" · ") || "—"}</td>
                <td className="px-4 py-3 text-muted">{c.seniority}</td>
                <td className="px-4 py-3 text-muted font-mono text-xs">
                  <span className="flex items-center gap-1.5">
                    {c.email || "—"}
                    {(c.email_source === "COMPANY_PATTERN" || c.email_verification_status === "INFERRED") && (
                      <span data-testid={`contact-inferred-${c.id}`} title="Email déduit du pattern entreprise (non vérifié)"
                        className="inline-flex items-center gap-1 text-[9px] font-sans font-semibold px-1.5 py-0.5 rounded bg-amber-500/12 text-amber-300 border border-amber-500/30"><Sparkle size={9} weight="fill" /> déduit</span>
                    )}
                  </span>
                </td>
              </Row>
            ))}
          </Table>
          <Paginator page={page} total={activeTotal} pageSize={pageSize} onPage={setPage} onPageSize={setPageSize} />
        </TabsContent>
      </Tabs>

      {/* À compléter — signaux dont l'entreprise n'a pas de domaine (masqués côté ESN) */}
      {tab === "incomplete" && (
        <div>
          <div className="surface rounded-lg p-4 mb-4 border-l-2 border-amber-500/50" data-testid="incomplete-banner">
            <div className="flex items-start gap-3">
              <WarningCircle size={20} weight="duotone" className="text-amber-400 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-muted">Ces signaux concernent des entreprises <strong className="text-fg">sans domaine web</strong> — indispensable pour que le moteur trouve les bons décideurs. Enrichissez le domaine (recherche web, puis Prospeo en dernier recours). Une fois trouvé, le signal peut redevenir visible côté ESN.</p>
                {gate && (
                  <p className="text-xs text-muted mt-1 font-mono">
                    Couverture domaine : <span className="text-fg">{gate.companies_with_domain}/{gate.companies_total}</span> entreprise(s)
                    {gate.last_bulk?.at && <> · dernier lot : {gate.last_bulk.found}/{gate.last_bulk.processed} trouvés</>}
                  </p>
                )}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-4 pt-3 border-t border-border">
              <button onClick={runBulk} disabled={bulkBusy} data-testid="bulk-enrich-btn"
                className="flex items-center gap-2 bg-accent text-black font-semibold text-xs px-4 py-2 rounded-md hover-lift disabled:opacity-60">
                <Globe size={14} weight="bold" /> {bulkBusy ? "Enrichissement lancé…" : "Enrichir en masse (web, 50)"}
              </button>
              <span className="flex items-center gap-1.5 h-9 px-3 rounded-md border bg-accent/12 border-accent/40 text-accent text-xs font-medium" data-testid="gate-always-on">
                <CheckCircle size={14} weight="fill" /> Masquage ESN : toujours actif
              </span>
              <span className="text-[11px] text-muted">Un ESN ne voit jamais un signal sans domaine (recherche de décideurs impossible sans domaine).</span>
            </div>
          </div>
          <div className="surface rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-border text-muted">
                    {["Intitulé", "Entreprise", "Ville", "Compétences", "Action"].map((h, i) => <th key={i} className="px-4 py-3 overline font-medium">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {incomplete.length === 0 && (
                    <tr><td colSpan={5} className="px-4 py-8 text-center text-muted text-sm">Aucun signal à compléter. 🎉</td></tr>
                  )}
                  {incomplete.map((s) => (
                    <tr key={s.id} className="border-b border-border/60 hover:bg-surface-2/40" data-testid={`incomplete-row-${s.id}`}>
                      <td className="px-4 py-3 text-fg font-medium">{s.role_title}</td>
                      <td className="px-4 py-3 text-muted">{s.company_name}</td>
                      <td className="px-4 py-3 text-muted">{s.city}</td>
                      <td className="px-4 py-3 text-muted text-xs font-mono truncate max-w-[200px]">{(s.skills || []).join(", ")}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button onClick={() => enrichDomain(s.company_id, s.company_name, "auto")} disabled={enriching === s.company_id}
                          data-testid={`enrich-domain-${s.company_id}`}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold border border-accent/40 text-accent px-3 py-1.5 rounded-md hover-lift disabled:opacity-50">
                          <Globe size={13} weight="duotone" /> {enriching === s.company_id ? "Recherche…" : "Enrichir (web + Prospeo)"}
                        </button>
                        <button onClick={() => enrichDomain(s.company_id, s.company_name, "web")} disabled={enriching === s.company_id}
                          data-testid={`enrich-web-${s.company_id}`}
                          className="ml-2 inline-flex items-center gap-1 text-[11px] text-muted hover:text-fg underline underline-offset-2 disabled:opacity-50">
                          web seul
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Domaine introuvable — entreprises où web + Prospeo ont échoué (signaux désactivés côté ESN) */}
      {tab === "domain-failed" && (
        <div>
          <div className="surface rounded-lg p-4 mb-4 border-l-2 border-red-500/50" data-testid="domain-failed-banner">
            <div className="flex items-start gap-3">
              <WarningCircle size={20} weight="duotone" className="text-red-400 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-muted">Entreprises pour lesquelles la recherche de domaine <strong className="text-fg">web + Prospeo a échoué</strong>. Leurs signaux ne sont <strong className="text-fg">jamais exposés aux ESN</strong> (recherche de décideurs impossible sans domaine). Vous pouvez réessayer l'enrichissement, saisir un domaine manuellement (bouton ✏️) ou supprimer l'entreprise.</p>
              </div>
            </div>
          </div>
          <div className="surface rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-border text-muted">
                    {["Entreprise", "Ville", "Signaux", "Action"].map((h, i) => <th key={i} className="px-4 py-3 overline font-medium">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {domainFailed.length === 0 && (
                    <tr><td colSpan={4} className="px-4 py-8 text-center text-muted text-sm">Aucune entreprise en « Domaine introuvable ». 🎉</td></tr>
                  )}
                  {domainFailed.map((c) => (
                    <tr key={c.id} className="border-b border-border/60 hover:bg-surface-2/40" data-testid={`domain-failed-row-${c.id}`}>
                      <td className="px-4 py-3 text-fg font-medium">{c.name}</td>
                      <td className="px-4 py-3 text-muted">{c.city || "—"}</td>
                      <td className="px-4 py-3 font-mono text-muted">{c.signals_count}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button onClick={() => enrichDomain(c.id, c.name, "auto")} disabled={enriching === c.id}
                          data-testid={`domain-failed-retry-${c.id}`}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold border border-accent/40 text-accent px-3 py-1.5 rounded-md hover-lift disabled:opacity-50">
                          <Globe size={13} weight="duotone" /> {enriching === c.id ? "Recherche…" : "Réessayer"}
                        </button>
                        <button onClick={() => openEdit(c)} title="Saisir un domaine manuellement"
                          data-testid={`domain-failed-edit-${c.id}`}
                          className="ml-1 p-2 rounded-md text-muted hover:text-accent hover:bg-surface-2"><PencilSimple size={15} /></button>
                        <button onClick={() => remove(c.id)} title="Supprimer + blacklister"
                          data-testid={`domain-failed-delete-${c.id}`}
                          className="p-2 rounded-md text-muted hover:text-destructive hover:bg-surface-2"><Trash size={15} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      {/* Entreprises exclues — signaux futurs bloqués à l'ingestion (économie crédits Prospeo) */}
      {tab === "excluded" && (
        <div>
          <div className="surface rounded-lg p-4 mb-4 border-l-2 border-amber-500/50" data-testid="excluded-banner">
            <div className="flex items-start gap-3">
              <Prohibit size={20} weight="duotone" className="text-amber-400 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-muted">Ces entreprises sont <strong className="text-fg">exclues des signaux</strong> : leurs <strong className="text-fg">futurs</strong> besoins sont automatiquement rejetés à l'ingestion (aucun signal créé, aucun enrichissement Prospeo/CRM) — pour économiser des crédits et réduire le bruit. L'entreprise et son historique restent en base. Cliquez « Réautoriser » pour réactiver la collecte.</p>
              </div>
            </div>
          </div>
          <div className="surface rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-border text-muted">
                    {["Entreprise", "Raison", "Note", "Exclue le", "Signaux", "Action"].map((h, i) => <th key={i} className="px-4 py-3 overline font-medium">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {excluded.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-muted text-sm">Aucune entreprise exclue.</td></tr>
                  )}
                  {excluded.map((c) => (
                    <tr key={c.id} className="border-b border-border/60 hover:bg-surface-2/40" data-testid={`excluded-row-${c.id}`}>
                      <td className="px-4 py-3 text-fg font-medium">{c.name}</td>
                      <td className="px-4 py-3 text-muted">{c.exclusionReason || "—"}</td>
                      <td className="px-4 py-3 text-muted text-xs max-w-[220px] truncate" title={c.exclusionNote || ""}>{c.exclusionNote || "—"}</td>
                      <td className="px-4 py-3 text-muted whitespace-nowrap font-mono text-xs">{c.excludedAt ? new Date(c.excludedAt).toLocaleDateString("fr-FR") : "—"}</td>
                      <td className="px-4 py-3 font-mono text-muted">{c.signals_count}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button onClick={() => allowSignals(c)} data-testid={`excluded-allow-${c.id}`}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold border border-emerald-500/40 text-emerald-300 px-3 py-1.5 rounded-md hover-lift">
                          <ArrowCounterClockwise size={13} weight="bold" /> Réautoriser
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Exclusion d'entreprise des signaux */}
      <Dialog open={!!excludeTarget} onOpenChange={(v) => { if (!v) setExcludeTarget(null); }}>
        <DialogContent className="bg-surface border-border max-w-lg" data-testid="exclude-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Prohibit size={20} weight="duotone" className="text-amber-400" /> Exclure des signaux</DialogTitle>
            <DialogDescription className="text-muted">
              <strong className="text-fg">{excludeTarget?.name}</strong> — ses futurs signaux seront rejetés à l'ingestion (aucun crédit Prospeo consommé). L'entreprise reste en base.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <Field label="Raison (optionnel)">
              <Select value={excludeForm.reason || "__none__"} onValueChange={(v) => setExcludeForm((f) => ({ ...f, reason: v === "__none__" ? "" : v }))}>
                <SelectTrigger className={inputCls} data-testid="exclude-reason"><SelectValue placeholder="Choisir une raison…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Aucune</SelectItem>
                  {EXCLUSION_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Note (optionnel)">
              <Textarea className={inputCls} value={excludeForm.note} onChange={(e) => setExcludeForm((f) => ({ ...f, note: e.target.value }))}
                placeholder="Contexte de l'exclusion…" data-testid="exclude-note" />
            </Field>
            <div>
              <label className="overline block mb-2">Signaux existants de cette entreprise</label>
              <div className="space-y-2">
                {[
                  { v: "keep", l: "Conserver les signaux existants", d: "Seuls les futurs signaux sont bloqués." },
                  { v: "archive", l: "Archiver les signaux existants", d: "Statut EXCLUDED + masqués des ESN (réversibles)." },
                  { v: "delete", l: "Supprimer les signaux existants", d: "Suppression définitive des signaux actuels." },
                ].map((o) => (
                  <label key={o.v} className={`flex items-start gap-2.5 p-2.5 rounded-md border cursor-pointer ${excludeForm.cleanup === o.v ? "border-accent/50 bg-accent/8" : "border-border hover:border-accent/30"}`}
                    data-testid={`exclude-cleanup-${o.v}`}>
                    <input type="radio" name="exclude-cleanup" checked={excludeForm.cleanup === o.v}
                      onChange={() => setExcludeForm((f) => ({ ...f, cleanup: o.v }))} className="accent-[#00E5FF] mt-0.5" />
                    <span>
                      <span className="text-sm text-fg block">{o.l}</span>
                      <span className="text-xs text-muted">{o.d}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <button onClick={() => setExcludeTarget(null)} className="border border-border text-fg font-semibold px-5 py-2.5 rounded-md hover-lift">Annuler</button>
            <button onClick={runExclude} disabled={excludeBusy} data-testid="exclude-confirm-btn"
              className="bg-amber-500 text-black font-semibold px-5 py-2.5 rounded-md hover-lift disabled:opacity-60">
              {excludeBusy ? "Exclusion…" : "Exclure des signaux"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-surface border-border max-w-lg max-h-[85vh] overflow-y-auto" data-testid="content-dialog">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Modifier" : "Ajouter"}</DialogTitle>
          <DialogDescription className="text-muted">{(tab === "companies" || tab === "domain-failed") ? "Entreprise" : tab === "signals" ? "Signal de recrutement" : "Contact décideur"}</DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            {(tab === "companies" || tab === "domain-failed") && (
              <>
                {!editing && (
                  <Field label="Entreprise déjà en base ? (rechercher pour la modifier)">
                    <CompanyCombobox key="co-find" value="" displayName="" allowCreate={false}
                      onSelect={(id) => pickExistingCompany(id)}
                      placeholder="Rechercher une entreprise existante…" />
                  </Field>
                )}
                <Field label="Nom"><Input className={inputCls} value={form.name || ""} onChange={upd("name")} data-testid="form-name" /></Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Secteur"><Input className={inputCls} value={form.sector || ""} onChange={upd("sector")} /></Field>
                  <Field label="Taille"><Input className={inputCls} value={form.size || ""} onChange={upd("size")} /></Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Ville"><Input className={inputCls} value={form.city || ""} onChange={upd("city")} /></Field>
                  <Field label="Région"><Input className={inputCls} value={form.region || ""} onChange={upd("region")} /></Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Latitude"><Input className={inputCls} type="number" value={form.lat ?? ""} onChange={upd("lat")} /></Field>
                  <Field label="Longitude"><Input className={inputCls} type="number" value={form.lng ?? ""} onChange={upd("lng")} /></Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Site web"><Input className={inputCls} value={form.website || ""} onChange={upd("website")} /></Field>
                  <Field label="Couleur"><Input className={inputCls} value={form.logo_color || ""} onChange={upd("logo_color")} /></Field>
                </div>
                <Field label="Description"><Textarea className={inputCls} value={form.description || ""} onChange={upd("description")} /></Field>
              </>
            )}

            {tab === "signals" && (
              <>
                <Field label="Entreprise">
                  <CompanyCombobox key={`sig-${editing?.id || "new"}`} value={form.company_id} displayName={form.company_name || ""} onSelect={selectCompany} />
                </Field>
                <Field label="Intitulé du poste"><Input className={inputCls} value={form.role_title || ""} onChange={upd("role_title")} data-testid="form-role" /></Field>
                <Field label="Compétences (séparées par des virgules)"><Input className={inputCls} value={form.skills || ""} onChange={upd("skills")} placeholder="AWS, Kubernetes, Terraform" /></Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Lieu de la mission *">
                    <Input className={inputCls} value={form.city || ""} onChange={upd("city")} placeholder="ex : Lille" data-testid="form-city" />
                  </Field>
                  <Field label="Région / Métropole">
                    <Input className={inputCls} value={form.region || ""} onChange={upd("region")} placeholder="ex : Hauts-de-France" data-testid="form-region" />
                  </Field>
                </div>
                <p className="text-xs text-muted -mt-2">Le lieu de la mission est requis : il permet aux ESN de trouver ce signal via leur recherche géographique (ex : « à 40 km de Lille »).</p>
                <Field label="Agence (implantation)">
                  <Select value={form.office_id || "__auto__"} disabled={!form.company_id}
                    onValueChange={(v) => setForm((f) => ({ ...f, office_id: v === "__auto__" ? "" : v }))}>
                    <SelectTrigger className={inputCls} data-testid="form-office"><SelectValue placeholder="Auto (selon la ville)" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__auto__" data-testid="form-office-auto">Auto (selon la ville)</SelectItem>
                      {formOffices.map((o) => (
                        <SelectItem key={o.id} value={o.id} data-testid={`form-office-${o.id}`}>
                          {(o.display_name || o.name)}{o.is_headquarters ? " · Siège" : ""}{o.city ? ` — ${o.city}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <p className="text-xs text-muted -mt-2">
                  Réaffectez ce signal à la bonne agence : la recherche de décideurs interKO s'ancre alors sur la ville de cette implantation.
                  {formOfficesLoading ? " Chargement des agences…" : (!form.company_id ? " Sélectionnez d'abord une entreprise." : (formOffices.length === 0 ? " Aucune implantation pour cette entreprise (gérez-les depuis l'onglet Entreprises)." : ""))}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Séniorité">
                    <Select value={form.seniority} onValueChange={upd("seniority")}>
                      <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                      <SelectContent>{SENIORITY.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Type de contrat">
                    <Select value={form.contract_type} onValueChange={upd("contract_type")}>
                      <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                      <SelectContent>{CONTRACT.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                </div>
                <Field label="Type de client">
                  <Select value={form.client_type || "end_client"} onValueChange={upd("client_type")}>
                    <SelectTrigger className={inputCls} data-testid="form-client-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="end_client" data-testid="client-type-end-client">Client Final</SelectItem>
                      <SelectItem value="intermediary" data-testid="client-type-intermediary">ESN / Intermédiaire</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Description"><Textarea className={inputCls} value={form.description || ""} onChange={upd("description")} /></Field>
                <Field label="Type de mission (réservé admin — non visible par les ESN)">
                  <Select value={(form.mission_types || [])[0] || "Régie"} onValueChange={(v) => setForm((f) => ({ ...f, mission_types: [v] }))}>
                    <SelectTrigger className={inputCls} data-testid="form-mission-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MISSION_TYPE_OPTIONS.map((t) => <SelectItem key={t} value={t} data-testid={`form-mission-${t}`}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <label className="flex items-center gap-2 text-sm text-fg/85 cursor-pointer">
                  <input type="checkbox" checked={!!form.esn_hidden} onChange={(e) => setForm((f) => ({ ...f, esn_hidden: e.target.checked }))}
                    data-testid="form-esn-hidden" className="accent-[#00E5FF]" />
                  Masquer ce signal aux ESN (recherche)
                </label>
              </>
            )}

            {tab === "contacts" && (
              <>
                <Field label="Entreprise">
                  <CompanyCombobox key={`ct-${editing?.id || "new"}`} value={form.company_id} displayName={form.company_name || ""} onSelect={selectCompany} />
                </Field>
                <Field label="Nom"><Input className={inputCls} value={form.name || ""} onChange={upd("name")} data-testid="form-name" /></Field>
                <Field label="Fonction"><Input className={inputCls} value={form.title || ""} onChange={upd("title")} /></Field>
                <Field label="Type">
                  <Select value={form.seniority} onValueChange={upd("seniority")}>
                    <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                    <SelectContent>{CONTACT_SENIORITY.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Email"><Input className={inputCls} value={form.email || ""} onChange={upd("email")} /></Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Ville"><Input className={inputCls} value={form.city || ""} onChange={upd("city")} data-testid="form-contact-city" placeholder="ex: Lille" /></Field>
                  <Field label="Région / Métropole"><Input className={inputCls} value={form.region || ""} onChange={upd("region")} data-testid="form-contact-region" placeholder="ex: Hauts-de-France" /></Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Téléphone"><Input className={inputCls} value={form.phone || ""} onChange={upd("phone")} /></Field>
                  <Field label="LinkedIn"><Input className={inputCls} value={form.linkedin || ""} onChange={upd("linkedin")} /></Field>
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <button onClick={save} data-testid="content-save-btn" className="bg-accent text-black font-semibold px-5 py-2.5 rounded-md hover-lift w-full">
              {editing ? "Enregistrer" : "Créer"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Import CSV Dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="bg-surface border-border max-w-lg" data-testid="import-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Importer un CSV — {tab === "companies" ? "Entreprises" : tab === "contacts" ? "Décideurs" : "Signaux"}</DialogTitle>
            <DialogDescription className="text-muted">{csvHint}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <input type="file" accept=".csv,text/csv" onChange={onCsvFile} data-testid="import-file"
              className="block w-full text-sm text-muted file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-surface-2 file:text-fg file:cursor-pointer" />
            <Textarea className={inputCls + " min-h-[160px] font-mono text-xs"} value={csvText} onChange={(e) => setCsvText(e.target.value)}
              placeholder="Ou collez le contenu CSV ici…" data-testid="import-textarea" />
          </div>
          <DialogFooter>
            <button onClick={runImport} disabled={importing} data-testid="import-run-btn"
              className="bg-accent text-black font-semibold px-5 py-2.5 rounded-md hover-lift w-full disabled:opacity-60">
              {importing ? "Import…" : "Importer"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SignalImportWizard open={wizardOpen} onOpenChange={setWizardOpen} onDone={refresh} />
      <ContactImportWizard open={contactWizardOpen} onOpenChange={setContactWizardOpen} onDone={refresh} />
      <CompanyImportWizard open={companyWizardOpen} onOpenChange={setCompanyWizardOpen} onDone={refresh} />
      <OfficesDialog company={officesFor} open={!!officesFor} onClose={() => { setOfficesFor(null); refresh(); }} />

      <Dialog open={dedupOpen} onOpenChange={(v) => { if (!(dedupJob && dedupJob.status === "running")) setDedupOpen(v); }}>
        <DialogContent className="bg-surface border-border max-w-lg" data-testid="dedup-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Broom size={20} /> Nettoyer les doublons</DialogTitle>
            <DialogDescription className="text-muted">
              Fusionne les entreprises et contacts en double. Les champs vides sont complétés depuis les doublons — aucune donnée existante n'est écrasée. Action irréversible.
            </DialogDescription>
          </DialogHeader>

          {dedupLoading && <div className="py-6 text-center text-muted" data-testid="dedup-loading">Analyse de la base…</div>}

          {dedupPreview && !dedupJob && (
            <div className="space-y-3 py-2 text-sm" data-testid="dedup-preview">
              <div className="grid grid-cols-2 gap-3">
                <div className="surface rounded-md p-3 border border-border">
                  <div className="overline mb-1">Entreprises</div>
                  <div className="text-2xl font-bold">{dedupPreview.companies_total}</div>
                  <div className="text-amber-300 mt-1">{dedupPreview.companies_to_merge} doublon(s) à fusionner</div>
                  <div className="text-muted text-xs mt-0.5">→ {dedupPreview.companies_after} après nettoyage</div>
                </div>
                <div className="surface rounded-md p-3 border border-border">
                  <div className="overline mb-1">Contacts</div>
                  <div className="text-2xl font-bold">{dedupPreview.contacts_total}</div>
                  <div className="text-amber-300 mt-1">{dedupPreview.contacts_to_merge} doublon(s) à fusionner</div>
                  <div className="text-muted text-xs mt-0.5">→ {dedupPreview.contacts_after} après nettoyage</div>
                </div>
              </div>
              {dedupPreview.companies_missing_normalized_name > 0 && (
                <div className="text-xs text-muted">{dedupPreview.companies_missing_normalized_name} entreprise(s) sans nom normalisé seront corrigées (fiabilise les imports futurs).</div>
              )}
              {dedupPreview.companies_to_merge === 0 && dedupPreview.contacts_to_merge === 0 && (
                <div className="text-emerald-400" data-testid="dedup-clean">Aucun doublon détecté — la base est propre ✔</div>
              )}
            </div>
          )}

          {dedupJob && (
            <div className="space-y-2 py-3 text-sm" data-testid="dedup-progress">
              {dedupJob.status === "running" && (
                <>
                  <div className="text-muted">Fusion en cours…{dedupJob.phase === "companies" ? " (entreprises)" : dedupJob.phase === "contacts" ? " (contacts)" : ""}</div>
                  {dedupJob.phase === "companies" && dedupJob.company_groups_total > 0 && (
                    <div>Entreprises : {dedupJob.company_groups_done || 0} / {dedupJob.company_groups_total} groupes</div>
                  )}
                  {dedupJob.phase === "contacts" && dedupJob.contact_groups_total > 0 && (
                    <div>Contacts : {dedupJob.contact_groups_done || 0} / {dedupJob.contact_groups_total} groupes</div>
                  )}
                  <div className="h-1.5 bg-surface-2 rounded overflow-hidden"><div className="h-full bg-accent animate-pulse" style={{ width: "60%" }} /></div>
                </>
              )}
              {dedupJob.status === "done" && (
                <div className="text-emerald-400" data-testid="dedup-done">Terminé — {dedupJob.merged_companies || 0} entreprise(s) et {dedupJob.merged_contacts || 0} contact(s) fusionnés puis supprimés.</div>
              )}
              {dedupJob.status === "error" && (
                <div className="text-red-400">Échec : {dedupJob.message || "erreur"}</div>
              )}
            </div>
          )}

          <DialogFooter>
            {dedupPreview && !dedupJob && (dedupPreview.companies_to_merge > 0 || dedupPreview.contacts_to_merge > 0) && (
              <button onClick={runDedup} data-testid="dedup-run-btn"
                className="bg-amber-500 text-black font-semibold px-5 py-2.5 rounded-md hover-lift w-full">
                Lancer la fusion
              </button>
            )}
            {dedupJob && dedupJob.status !== "running" && (
              <button onClick={() => setDedupOpen(false)} data-testid="dedup-close-btn"
                className="border border-border text-fg font-semibold px-5 py-2.5 rounded-md hover-lift w-full">Fermer</button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Table({ headers, children, sort, order, onSort }) {
  return (
    <div className="surface rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-border text-muted">
              {headers.map((h, i) => {
                const label = typeof h === "string" ? h : h.label;
                const field = typeof h === "object" ? h.field : null;
                const node = typeof h === "object" ? h.node : null;
                if (field && onSort) {
                  const active = sort === field;
                  return (
                    <th key={i} className="px-4 py-3">
                      <button onClick={() => onSort(field)} data-testid={`sort-${field}`}
                        className={`flex items-center gap-1 overline font-medium hover:text-fg ${active ? "text-accent" : ""}`}>
                        {label}
                        {active ? (order === "asc" ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />) : <CaretUpDown size={12} className="opacity-30" />}
                      </button>
                    </th>
                  );
                }
                return <th key={i} className="px-4 py-3 overline font-medium">{node || label}</th>;
              })}
            </tr>
          </thead>
          <tbody>{children}</tbody>
        </table>
      </div>
    </div>
  );
}

function Paginator({ page, total, pageSize, onPage, onPageSize }) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="flex items-center justify-between gap-3 mt-4 flex-wrap" data-testid="paginator">
      <div className="flex items-center gap-1.5 text-xs text-muted">
        <span className="mr-1">Lignes par page :</span>
        {[25, 50, 100].map((n) => (
          <button key={n} onClick={() => onPageSize(n)} data-testid={`page-size-${n}`}
            className={`px-2.5 py-1 rounded border font-mono ${pageSize === n ? "border-accent text-accent bg-accent/10" : "border-border text-muted hover:text-fg"}`}>{n}</button>
        ))}
      </div>
      <div className="flex items-center gap-2 text-xs">
        <span className="font-mono text-muted" data-testid="page-info">{total} résultat{total > 1 ? "s" : ""} · page {page}/{totalPages}</span>
        <button disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid="page-prev"
          className="flex items-center gap-1 px-3 py-1.5 rounded border border-border text-fg disabled:opacity-40 hover:border-accent/40"><CaretLeft size={12} /> Préc.</button>
        <button disabled={page >= totalPages} onClick={() => onPage(page + 1)} data-testid="page-next"
          className="flex items-center gap-1 px-3 py-1.5 rounded border border-border text-fg disabled:opacity-40 hover:border-accent/40">Suiv. <CaretRight size={12} /></button>
      </div>
    </div>
  );
}

function Row({ children, onEdit, onDelete, testid, extraActions }) {
  return (
    <tr className="border-b border-border/60 hover:bg-surface-2/40" data-testid={testid}>
      {children}
      <td className="px-4 py-3 text-right whitespace-nowrap">
        {extraActions}
        <button onClick={onEdit} className="p-2 rounded-md text-muted hover:text-accent hover:bg-surface-2" data-testid={`${testid}-edit`}><PencilSimple size={15} /></button>
        <button onClick={onDelete} className="p-2 rounded-md text-muted hover:text-destructive hover:bg-surface-2" data-testid={`${testid}-delete`}><Trash size={15} /></button>
      </td>
    </tr>
  );
}
